import React from 'react';
import WarningModal from '../../Common/Modals/WarningModal/WarningModal';
import DiffModal from '../../Common/Modals/DiffModal/DiffModal';
import ComponentType from '../../../types/report/ComponentType';
import httpClient from '../../../infrastructure/helpers/common/httpClient';
import reportConfigAdapter from '../../../infrastructure/helpers/report/import/reportConfigAdapter';
import ChangeDescription from '../../../infrastructure/types/DifferenceVisualizer/ChangeDescription';
import GlobalModuleComponentsAnalysisResult from './Types/GlobalModuleComponentsAnalysisResult';
import GlobalModuleConfig from '../../../types/report/GlobalModuleConfig';
import ReportConfig from '../../../types/report/ReportConfig';
import DataSourceAnalysisResult from './Types/DataSourceAnalysisResult';
import ReportConfigBundle from '../../../types/report/import/ReportConfigBundle';
import UserDefinedFunction from '../../../types/report/UserDefinedFunction';
import Assignment from '../../../types/functions/Assignment/Assignment';
import Condition from '../../../types/functions/Condition/Condition';
import Lookup from '../../../types/functions/Lookup';
import ReportVariable from '../../../types/report/ReportVariable';
import copyObject from '../../../infrastructure/helpers/common/copyObject';
import { getColumns, getComponentNameExistsError, getCurrentGlobalModuleConfig, resolveMissingComponents, updateLookupsDataSources, updateReportConfigGlobalComponents } from './GlobalModule.Helpers';
import ImportConfigState from './Types/ImportConfigState';
import ImportReportConfigState from './ImportReportConfigState';
import { importReportConfig } from './ServerCalls';
import ComponentInPopup from './Types/ComponentInPopup';
import UiDataGrid from '../../Common/DataGrid/UiDataGrid/UiDataGrid';
import stringHelper from '../../../infrastructure/helpers/common/stringHelper';
import copyArray from '../../../infrastructure/helpers/common/copyArray';
import lookupValidator from '../../../infrastructure/helpers/functions/validator/lookupValidator';
import variableValidator from '../../../infrastructure/helpers/functions/validator/variableValidator';
import userDefinedFunctionValidator from '../../../infrastructure/helpers/functions/validator/userDefinedFunctionValidator';
import Mode from '../../../types/report/Mode';
import IngestionConfig from '../../../types/ingestion/IngestionConfig';
import RefDataSource from '../../../types/report/RefDataSource';
import operandHelper from '../../../infrastructure/helpers/functions/operand/operandHelper';
import Operand from '../../../types/functions/Operand/Operand';

interface MismachedComponents {
    lookupsForUpdate: Lookup[],
    lookupsForAdd: Lookup[],
    variablesForUpdate: ReportVariable[],
    variablesForAdd: ReportVariable[],
    udfsForUpdate: UserDefinedFunction[],
    udfsForAdd: UserDefinedFunction[]
}

interface Props {
    onMismatchedGlobalComponentsOkClick: () => void,
    onCancelClick: () => void,
    dataSourceAnalysisResult: DataSourceAnalysisResult,
    currentGlobalModuleConfig: GlobalModuleConfig,
    bundle: ReportConfigBundle,
    globalModuleComponentsAnalysisResult: GlobalModuleComponentsAnalysisResult,
    setState: (state: ImportConfigState) => void,
    onChange: (state: ImportReportConfigState) => void
    componentsChanges: ChangeDescription[],
    components: ComponentInPopup[],
    dataSources: IngestionConfig[];
}

interface State {
    showComponentsChanges: boolean,
    diffLeftLogic: string,
    diffRightLogic: string,
    globalModuleComponentsAnalysisResult: GlobalModuleComponentsAnalysisResult,
    currentStepIndex: number,
    resultComponents: ComponentInPopup[],
    showSpinner: boolean
}

class GlobalModuleComponents extends React.Component<Props, State> {

    constructor(props: Props) {
        super(props);

        this.state = {
            showComponentsChanges: false,
            diffLeftLogic: '',
            diffRightLogic: '',
            globalModuleComponentsAnalysisResult: {} as GlobalModuleComponentsAnalysisResult,
            currentStepIndex: 0,
            resultComponents: [],
            showSpinner: false
        }

        this.onCompareLogicClick = this.onCompareLogicClick.bind(this);
        this.onComponentNamed = this.onComponentNamed.bind(this);
        this.setComponentUseUpdateLogic = this.setComponentUseUpdateLogic.bind(this);
        this.onCompareLogicClick = this.onCompareLogicClick.bind(this);
        this.onMismatchedGlobalComponentsOkClick = this.onMismatchedGlobalComponentsOkClick.bind(this);
    }

    onCompareLogicClick(name: string, componentType: ComponentType): void {

        let leftLogic = '';

        let rightLogic = '';

        let noDiff = 'n/a';

        switch (componentType.toString()) {
            case ComponentType.Lookup: leftLogic = this.getLogic('Lookup', true, name) || noDiff;
                rightLogic = this.getLogic('Lookup', false, name) || noDiff;
                break;
            case ComponentType.Variable: leftLogic = this.getLogic('Variable', true, name) || noDiff;
                rightLogic = this.getLogic('Variable', false, name) || noDiff;
                break;
            case 'User-Defined Function Parameter': leftLogic = this.getLogic('User-Defined Functions', true, name) || noDiff;
                rightLogic = this.getLogic('User-Defined Functions', false, name) || noDiff;
                break;

            case 'User-Defined Functions': leftLogic = this.getLogic('User-Defined Functions', true, name) || noDiff;
                rightLogic = this.getLogic('User-Defined Functions', false, name) || noDiff;
                break;

            default: break;
        }

        this.setState({ showComponentsChanges: true, diffLeftLogic: leftLogic, diffRightLogic: rightLogic });
    }

    getLogic(module: string, isLeftLogic: boolean, name: string): string {
        return this.props.componentsChanges.filter(x => x.module === module &&
            stringHelper.areEqualToLower(x.moduleName, name)).map(x => isLeftLogic ? x.previous : x.current).join('');
    }

    prepareGlobalConfigForUpdate(): GlobalModuleConfig {

        let currentGlobalModuleConfig = copyObject(this.props.currentGlobalModuleConfig);

        let globalModuleConfig = copyObject(this.props.bundle.globalModuleConfig as GlobalModuleConfig);

        this.resolveMismatchedComponents(globalModuleConfig, currentGlobalModuleConfig);

        resolveMissingComponents(currentGlobalModuleConfig, this.props.globalModuleComponentsAnalysisResult, this.props.dataSourceAnalysisResult.maps);

        this.addNewDataSources(currentGlobalModuleConfig.refDataSources);

        this.prepareComponents(currentGlobalModuleConfig);

        return currentGlobalModuleConfig;
    }

    addNewDataSources(dataSources: RefDataSource[]) {

        let ids = dataSources.map(x => x.id);

        this.props.dataSources.forEach((x) => {

            if (!ids.includes(x.id as number)) {
                dataSources.push({
                    id: x.id,
                    dataSouceKind: x.dataSourceKind,
                    fieldDefinitions: x.fieldDefinitions,
                    name: x.name
                } as RefDataSource);
            }
        });
    }

    resolveMismatchedComponents(globalModuleConfig: GlobalModuleConfig, currentGlobalModuleConfig: GlobalModuleConfig) {

        let mismatchedComponents = {
            lookupsForAdd: [],
            lookupsForUpdate: [],
            udfsForAdd: [],
            udfsForUpdate: [],
            variablesForAdd: [],
            variablesForUpdate: []
        } as MismachedComponents;

        this.resolveMismatchedLookups(mismatchedComponents, globalModuleConfig.lookups);

        this.resolveMismatchedVariables(mismatchedComponents, globalModuleConfig.variables);

        this.resolveMismatchedUserDefinedFunctions(mismatchedComponents, globalModuleConfig.userDefinedFunctions);

        this.fillCurrentGlobalModule(currentGlobalModuleConfig, mismatchedComponents);
    }

    fillCurrentGlobalModule(currentGlobalModuleConfig: GlobalModuleConfig, mismatchedComponents: MismachedComponents): void {
        let number = currentGlobalModuleConfig.lookups.length + 1;

        mismatchedComponents.lookupsForAdd.forEach((l) => {
            l.number = number;

            currentGlobalModuleConfig.lookups.push(l);

            number++;
        });

        let names = mismatchedComponents.lookupsForUpdate.map(x => x.name);

        currentGlobalModuleConfig.lookups.forEach((l) => {

            let lookup = mismatchedComponents.lookupsForUpdate.find(x => x.name === l.name);

            if (lookup) {
                lookup.condition = l.condition;
            }
        });

        number = currentGlobalModuleConfig.variables.length + 1;

        mismatchedComponents.variablesForAdd.forEach((l) => {
            l.number = number;

            currentGlobalModuleConfig.variables.push(l);

            number++;
        });

        currentGlobalModuleConfig.variables.forEach((v) => {

            let variable = mismatchedComponents.variablesForUpdate.find(x => x.name === v.name);

            if (variable) {
                variable.assignment = v.assignment;
            }
        });

        number = currentGlobalModuleConfig.userDefinedFunctions.length + 1;

        mismatchedComponents.udfsForAdd.forEach((func) => {
            func.number = number;

            currentGlobalModuleConfig.userDefinedFunctions.push(func);

            number++;
        });

        names = mismatchedComponents.udfsForUpdate.map(x => x.name);

        currentGlobalModuleConfig.userDefinedFunctions.forEach((func) => {

            let udf = mismatchedComponents.udfsForUpdate.find(x => x.name === func.name);

            if (udf) {
                udf.assignment = func.assignment;
            }
        });
    }

    resolveMismatchedLookups(mismatchedComponents: MismachedComponents, allLookups: Lookup[]) {

        let componentsNamesForUpdate = this.state.resultComponents.filter(x => x.useUpdateLogic && x.componentType === ComponentType.Lookup).map(x => x.name);

        mismatchedComponents.lookupsForUpdate = allLookups.filter(x => componentsNamesForUpdate.includes(x.name));

        updateLookupsDataSources(mismatchedComponents.lookupsForUpdate, this.props.dataSourceAnalysisResult.maps);

        let lookupsToAdd = this.state.resultComponents.filter(x => !x.useUpdateLogic && x.componentType === ComponentType.Lookup);

        allLookups.forEach((lookup) => {

            let component = lookupsToAdd.find(x => x.name.toLowerCase() === lookup.name.toLowerCase());

            if (component) {
                let newLookup = copyObject(lookup);

                updateLookupsDataSources([newLookup], this.props.dataSourceAnalysisResult.maps);

                newLookup.name = component.newName as string;
                newLookup.mode = Mode.Global;
                newLookup.number = allLookups.length + 1;
                newLookup.globalLookupModuleId =

                    mismatchedComponents.lookupsForAdd.push(newLookup);
            }
        });
    }

    resolveMismatchedVariables(mismatchedComponents: MismachedComponents, allVariables: ReportVariable[]) {

        let componentsNamesForUpdate = this.state.resultComponents.filter(x => x.useUpdateLogic && x.componentType === ComponentType.Variable).map(x => x.name);

        mismatchedComponents.variablesForUpdate = allVariables.filter(x => componentsNamesForUpdate.includes(x.name));

        let variablesToAdd = this.state.resultComponents.filter(x => !x.useUpdateLogic && x.componentType === ComponentType.Variable);

        allVariables.forEach((v) => {

            let component = variablesToAdd.find(x => x.name.toLowerCase() === v.name.toLowerCase());

            if (component) {
                let newVariable = copyObject(v);

                newVariable.name = component.newName as string;
                newVariable.number = allVariables.length + 1;

                mismatchedComponents.variablesForAdd.push(newVariable);
            }
        });
    }

    resolveMismatchedUserDefinedFunctions(mismatchedComponents: MismachedComponents, allFunctions: UserDefinedFunction[]) {

        let componentsNamesForUpdate = this.state.resultComponents.filter(x => x.useUpdateLogic && x.componentType === ComponentType.UserDefinedFunction).map(x => x.name);

        mismatchedComponents.udfsForUpdate = allFunctions.filter(x => componentsNamesForUpdate.includes(x.name));

        let udfsToAdd = this.state.resultComponents.filter(x => !x.useUpdateLogic && x.componentType === ComponentType.UserDefinedFunction);

        allFunctions.forEach((udf) => {

            let component = udfsToAdd.find(x => x.name.toLowerCase() === udf.name.toLowerCase());

            if (component) {
                let newUdf = copyObject(udf);

                newUdf.name = component.newName as string;
                newUdf.number = allFunctions.length + 1;

                mismatchedComponents.udfsForAdd.push(newUdf);
            }
        });
    }

    onComponentNamed(oldName: string, newName: string, componentType: ComponentType): string {

        let components = copyObject(this.state.resultComponents.length ? this.state.resultComponents : this.props.components);

        let component = components.find(x => x.name === oldName && x.componentType === componentType) as ComponentInPopup;

        component.newName = newName;

        let error = '';

        let componentWithSameName = '';
        switch (componentType) {
            case ComponentType.Lookup: componentWithSameName = this.props.currentGlobalModuleConfig.lookups.find(x => x.name === newName)?.name || ''; break;
            case ComponentType.Variable: componentWithSameName = this.props.currentGlobalModuleConfig.variables.find(x => x.name === newName)?.name || ''; break;
            case ComponentType.UserDefinedFunction: componentWithSameName = this.props.currentGlobalModuleConfig.userDefinedFunctions.find(x => x.name === newName)?.name || ''; break;

            default: break;
        }
        if (componentWithSameName) {
            error = getComponentNameExistsError(componentType, newName);
        }

        component.error = error;

        this.setState({ resultComponents: components });

        return error;
    }

    onMismatchedGlobalComponentsOkClick() {

        let errors = this.validateMismatchedComponentsFromPopup();

        if (!errors) {
            this.props.setState({ showSpinner: true } as ImportConfigState);

            this.setState({ showSpinner: true });

            this.proceedOnMismatchesResolved();
        }
    }

    validateMismatchedComponentsFromPopup(): boolean {

        if (!this.state.resultComponents.length) {

            let components = copyArray(this.props.components);

            components.forEach((c) => { c.error = 'Please add name or switch to Replace Logic'; });

            this.setState({ resultComponents: components });

            return true;
        }

        let components = copyArray(this.state.resultComponents);

        let hasErrors = false;

        components.forEach((c) => {

            c.error = '';

            if (!c.useUpdateLogic && !c.newName) {
                c.error = 'Please add name or switch to Replace Logic';

                hasErrors = true;
            }

            if (!hasErrors && !c.useUpdateLogic) {
                switch (c.componentType) {
                    case ComponentType.Lookup: hasErrors = this.validateLookupName(c); break;
                    case ComponentType.Variable: hasErrors = this.validateVariableName(c); break;
                    case ComponentType.UserDefinedFunction: hasErrors = this.validateUserDefinedFunctionName(c); break;

                    default: break;
                }
            }
        });

        this.setState({ resultComponents: components });

        return hasErrors;
    }

    validateLookupName(component: ComponentInPopup): boolean {
        let error = lookupValidator.validateMetadata(component.newName || '');

        if (error) {
            component.error = error || '';
        }

        return error?.length ? true : false;
    }

    validateVariableName(component: ComponentInPopup): boolean {
        let error = variableValidator.validateMetadata(component.newName || '');

        if (error) {
            component.error = error || '';
        }

        return error?.length ? true : false;
    }

    validateUserDefinedFunctionName(component: ComponentInPopup): boolean {
        let error = userDefinedFunctionValidator.validateMetadata(component.newName || '');

        if (error) {
            component.error = error || '';
        }

        return error?.length ? true : false;
    }

    async proceedOnMismatchesResolved() {

        try {
            let currentGlobalModuleConfig = this.prepareGlobalConfigForUpdate();

            await httpClient.put('report/global-module', currentGlobalModuleConfig);

            let updatedGlobalModuleConfig = await getCurrentGlobalModuleConfig();

            let reportConfig = copyObject(this.props.bundle.config);

            updateReportConfigGlobalComponents(reportConfig, updatedGlobalModuleConfig, this.state.resultComponents);

            reportConfigAdapter.adapt(reportConfig, this.props.dataSourceAnalysisResult.maps);

            await importReportConfig({ reportConfig, globalFunctions: updatedGlobalModuleConfig.userDefinedFunctions });

            this.setState({ showSpinner: false });

            this.props.onMismatchedGlobalComponentsOkClick();
        }
        catch (error) {
            this.props.setState({ showSpinner: false } as ImportConfigState);

            this.setState({ showSpinner: false });
        }
    }

    updateConfigDataSourcesWithTheOneFromDb(config: ReportConfig): void {
        config.refDataSources.forEach((ds) => {

            let id = this.props.dataSourceAnalysisResult.maps.dataSource[ds.id];

            if (id) {
                ds.id = id;
            }
        });
    }

    setComponentUseUpdateLogic(name: string, componentType: ComponentType, useUpdateLogic: boolean) {
        let components = copyObject(this.state.resultComponents.length ? this.state.resultComponents : this.props.components);

        let comp = components.find(x => x.name === name && x.componentType === componentType) as ComponentInPopup;

        comp.useUpdateLogic = useUpdateLogic;
        comp.showNewNameInput = !useUpdateLogic;
        comp.error = '';

        this.setState({ resultComponents: components });
    }

    onUpdateComponentLogicClick(name: string, componentType: ComponentType) {

        switch (componentType) {
            case ComponentType.Lookup: this.updateGlobalLookupLogic(name); break;
            case ComponentType.UserDefinedFunction: this.updateUserDefinedFunctionLogic(name); break;
            case ComponentType.Variable: this.updatVariableLogic(name); break;

            default: break;
        }
    }

    updateUserDefinedFunctionLogic(name: string) {

        let globalConfigCopy = copyObject(this.props.currentGlobalModuleConfig);

        let udf = globalConfigCopy.userDefinedFunctions.find(x => x.name.toLowerCase() === name.toLowerCase()) as UserDefinedFunction;

        udf.assignment = this.props.bundle.config.userDefinedFunctions.find(x => x.name.toLowerCase() === name.toLowerCase())?.assignment as Assignment;

        this.props.setState({ currentGlobalModuleConfig: globalConfigCopy } as ImportConfigState);
    }

    prepareComponents(reportConfig: GlobalModuleConfig) {

        reportConfig.lookups.forEach((l) => {
            operandHelper.proceedWhitCondition(l.condition, (operand: Operand) => {
                if (operand.fieldId && this.props.dataSourceAnalysisResult.maps.dataSourceField[operand.fieldId]) {
                    operand.fieldId = this.props.dataSourceAnalysisResult.maps.dataSourceField[operand.fieldId];
                }
            });
        });

        reportConfig.variables.forEach((v) => {
            operandHelper.proceedWhitAssignment(v.assignment, (operand: Operand) => {
                if (operand.fieldId && this.props.dataSourceAnalysisResult.maps.dataSourceField[operand.fieldId]) {
                    operand.fieldId = this.props.dataSourceAnalysisResult.maps.dataSourceField[operand.fieldId];
                }
            });
        });

        reportConfig.userDefinedFunctions.forEach((udf) => {
            operandHelper.proceedWhitAssignment(udf.assignment, (operand: Operand) => {
                if (operand.fieldId && this.props.dataSourceAnalysisResult.maps.dataSourceField[operand.fieldId]) {
                    operand.fieldId = this.props.dataSourceAnalysisResult.maps.dataSourceField[operand.fieldId];
                }
            });
        });
    }

    updatVariableLogic(name: string) {

        let globalConfigCopy = copyObject(this.props.currentGlobalModuleConfig);

        let variable = globalConfigCopy.variables.find(x => x.name.toLowerCase() === name.toLowerCase()) as ReportVariable;

        variable.assignment = this.props.bundle.config.variables.find(x => x.name.toLowerCase() === name.toLowerCase())?.assignment as Assignment;

        this.props.setState({ currentGlobalModuleConfig: globalConfigCopy } as ImportConfigState);
    }

    updateGlobalLookupLogic(name: string) {

        let globalConfigCopy: GlobalModuleConfig = copyObject(this.props.currentGlobalModuleConfig);

        let lookup = globalConfigCopy.lookups.find(x => x.name.toLowerCase() === name.toLowerCase()) as Lookup;

        lookup.condition = this.props.bundle.config.lookups.find(x => x.name.toLowerCase() === name.toLowerCase())?.condition as Condition;

        this.props.setState({ currentGlobalModuleConfig: globalConfigCopy } as ImportConfigState);
    }

    render(): JSX.Element {

        return (
            <>
                <WarningModal
                    size={"extra-large"}
                    onOkClick={this.onMismatchedGlobalComponentsOkClick}
                    onCancelClick={this.props.onCancelClick}
                    title='Mismatched Global module components'
                    message=''
                    showSpinnerOnOkClick={this.state.showSpinner}>
                    <UiDataGrid
                        tableClassName='global-components'
                        columns={getColumns(this.onComponentNamed, this.setComponentUseUpdateLogic, this.onCompareLogicClick)}
                        data={this.state.resultComponents.length ? this.state.resultComponents : this.props.components}
                        getKey={(x, i) => `${i}-${x.name}`} />
                </WarningModal>
                {this.state.showComponentsChanges &&
                    <DiffModal
                        left={this.state.diffLeftLogic}
                        right={this.state.diffRightLogic}
                        rightLabel='Report Config'
                        leftLabel='Global Config'
                        onClose={() => this.setState({ showComponentsChanges: false })}
                    />
                }
            </>
        );
    }
}

export default GlobalModuleComponents;
